import { Client, IMessage } from '@stomp/stompjs';

import { authenticationService } from '@aqx-core/common/services/auth';

import { formatTopicId, formatTopicPath } from '../utils';
import { ISocketTopic } from './types';

export enum EDealerMessageType {
  DEALER_EXPOSURE = 'DEALER_EXPOSURE'
}

export interface IDealerExposureMessageData {
  S: string; // symbol (ETHUSD.ecn)
  T: number; // type (0 - BUY, 1 - SELL)
  V: number; // volume (10)
  LS: number; // lotSize
  U: number; // units (100)
  EP: number; // entryPrice
  LT: number; // latestTradeTime (1755452681928) - Milliseconds
}

export interface IDealerExposureMessage {
  M: EDealerMessageType.DEALER_EXPOSURE;
  D: IDealerExposureMessageData;
}

export type TDealerTopicMessage = IDealerExposureMessage;

export interface IDealerTopicReceiver {
  subscriptionKey: string;
  payload: (payload: TDealerTopicMessage | undefined, symbol?: string) => void;
}

interface ISubscribeParams {
  symbol: string;
  receiver: IDealerTopicReceiver;
}

interface IProps {
  client: Client;
}

class DealerTopic implements ISocketTopic {
  private client: Client;
  private receivers: Record<string, Set<IDealerTopicReceiver> | undefined> = {};

  constructor(props: IProps) {
    const { client } = props;

    this.client = client;
  }

  public subscribe = (params: ISubscribeParams) => {
    const { symbol, receiver } = params;
    const { subPath, topicId } = this.mapTopicIds(symbol);

    if (!this.client) {
      return;
    }

    if (this.receivers[subPath]) {
      let isKeyExisting = false;
      this.receivers[subPath]?.forEach((r) => {
        isKeyExisting = r.subscriptionKey === receiver.subscriptionKey;
      });

      if (!isKeyExisting) {
        this.receivers[subPath]?.add(receiver);
      }
    } else {
      this.receivers[subPath] = new Set([receiver]);
      try {
        this.client.subscribe(formatTopicPath(subPath), (message) => this.onReceive(subPath, message, symbol), {
          id: topicId
        });
      } catch (err) {}
    }
  };

  public unsubscribe = (params: ISubscribeParams) => {
    const { symbol, receiver } = params;
    const { subPath, topicId } = this.mapTopicIds(symbol);

    if (!this.client) {
      return;
    }

    if (this.receivers[subPath]?.size !== 0) {
      let lastItem = null;
      this.receivers[subPath]?.forEach((r) => {
        if (r.subscriptionKey === receiver.subscriptionKey) {
          lastItem = r;
        }
      });

      if (lastItem) {
        this.receivers[subPath]?.delete(lastItem);
      }
    }

    if (this.receivers[subPath]?.size === 0) {
      try {
        this.client?.unsubscribe(topicId);
      } catch (err) {}

      this.receivers[subPath] = undefined;
      delete this.receivers[subPath];
    }
  };

  public clearSubscriptions = () => {
    this.receivers = {};
  };

  public reconnect = () => {
    const { receivers } = this;

    if (receivers) {
      Object.keys(receivers).forEach((subPath) => {
        this.client.subscribe(formatTopicPath(subPath), (message) => this.onReceive(subPath, message), {
          id: formatTopicId(subPath)
        });
      });
    }
  };

  private onReceive = (id: string, message: IMessage, symbol?: string) => {
    const receivers = this.receivers[id];

    if (!receivers || !message) {
      return;
    }

    const payload = JSON.parse(message.body) as TDealerTopicMessage;

    receivers.forEach((receiver) => receiver.payload(payload, symbol));
  };

  private mapTopicIds = (symbol: string) => {
    const { tradingAccounts } = authenticationService.context;
    const selectedAccount = tradingAccounts?.find((account) => account.selected);
    const omsServerId = selectedAccount?.omsServerId;

    const subPath = `dealer/${omsServerId}/${symbol}`;

    return {
      subPath,
      topicId: formatTopicId(subPath)
    };
  };
}

export default DealerTopic;
